import React from 'react';
import { Button, Badge, Avatar } from '../ui';
import type { VoiceConnectionStatus } from '../../types/voice-demo';
import type { PartnerUser } from '../../hooks/requests/queries/use_all_users_query';

interface VoiceControlsProps {
  status: VoiceConnectionStatus;
  isMuted: boolean;
  selectedUser: PartnerUser;
  hasConversation?: boolean;
  isDeletingConversation?: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onToggleMute: () => void;
  onDeleteConversation?: () => void;
}

const statusConfig: Record<
  VoiceConnectionStatus,
  { label: string; variant: 'danger' | 'success' | 'warning' | 'info' }
> = {
  disconnected: { label: 'Disconnected', variant: 'danger' },
  connecting: { label: 'Connecting...', variant: 'warning' },
  connected: { label: 'Waiting for Ally...', variant: 'info' },
  recording: { label: 'Recording', variant: 'success' },
};

const VoiceControls: React.FC<VoiceControlsProps> = ({
  status,
  isMuted,
  selectedUser,
  hasConversation = false,
  isDeletingConversation = false,
  onConnect,
  onDisconnect,
  onToggleMute,
  onDeleteConversation,
}) => {
  const isActive = status !== 'disconnected';
  const canMute = status === 'recording' || status === 'connected';
  const { label, variant } = statusConfig[status];

  return (
    <div className="pb-4 mb-4 border-b border-au-divider">
      <div className="flex items-center gap-3 flex-wrap">
        {/* User info */}
        <div className="flex items-center gap-3 mr-2">
          <Avatar size="sm" name={selectedUser.first_name || selectedUser.user_id} />
          <div className="min-w-0">
            <div className="font-medium text-sm text-au-text-primary">
              {selectedUser.first_name || selectedUser.user_id}
            </div>
            <div className="text-xs text-au-text-tertiary">
              {selectedUser.company_name || selectedUser.company_id}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-au-divider hidden sm:block" />

        {/* Controls */}
        {!isActive ? (
          <Button variant="primary" size="sm" onClick={onConnect}>
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              Connect
            </span>
          </Button>
        ) : (
          <Button variant="danger" size="sm" onClick={onDisconnect}>
            Disconnect
          </Button>
        )}

        {/* Delete conversation — only when disconnected and a conversation exists */}
        {!isActive && hasConversation && onDeleteConversation && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDeleteConversation}
            isLoading={isDeletingConversation}
            title="Delete conversation"
          >
            <span className="flex items-center gap-1.5 text-red-500 hover:text-red-600">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete conversation
            </span>
          </Button>
        )}

        <Button
          variant={isMuted ? 'warning' : 'ghost'}
          size="sm"
          onClick={onToggleMute}
          disabled={!canMute}
        >
          {isMuted ? 'Unmute' : 'Mute'}
        </Button>

        {/* Status badge - pushed to the right */}
        <div className="ml-auto">
          <Badge variant={variant} dot>
            {label}
          </Badge>
        </div>
      </div>
    </div>
  );
};

export default VoiceControls;
